import * as $ from "jquery";
import * as _ from "lodash";
import { DailyReportFilesCollection } from "../../../collections/DailyReportFiles";
import { BaseInternalView, BaseInternalViewOptions } from "../../_BaseViews";

export interface ReportsViewOptions extends BaseInternalViewOptions {}

export class DailyReportsView extends BaseInternalView {
    private template = _.template(require("../../../templates/reports/daily_reports/dailyReports.html"));
    private orgId: number;
    private dailyReportFilesCollection: DailyReportFilesCollection;

    constructor(options: ReportsViewOptions) {
        super(
            <any>_.extend(
                {
                    events: {
                        "click .button__download--dailyreport": "handleDownloadReportFile",
                        "click .month-toggle-button": "handleToggleMonth",
                    },
                },
                options
            )
        );
    }

    private ensureCollectionsLoaded() {
        var outerDeferred = $.Deferred();
        $.when(this.dailyReportFilesCollection.getCollectionFetchPromise())
            .then(() => {
                outerDeferred.resolve();
            })
            .catch((error) => {
                outerDeferred.reject(error); // Pass the error to the deferred rejection
            });
        return outerDeferred;
    }

    render() {
        this.renderMain(false);
        $.when(this.ensureCollectionsLoaded())
            .then(() => {
                this.renderMain(true, this.orgId);
            })
            .catch((error) => {
                console.log("error", error);
                (<any>$).notify(
                    {
                        title: "Something went wrong",
                        message: "Please try again later",
                        icon: "fa fa-exclamation-circle",
                    },
                    {
                        animate: {
                            enter: "animated fadeInUp",
                        },
                        // The type property defines the css style
                        // See alerts.less for available styles
                        type: `warning-alt`,
                        placement: { from: "bottom", align: "center" },
                        delay: 4000,
                    }
                );
            });

        return this;
    }

    show(orgId) {
        // Get organization id from the router
        this.orgId = orgId;
        this.dailyReportFilesCollection = new DailyReportFilesCollection([], {
            organizationId: this.orgId,
        });
        this.render();
    }

    private renderMain(collectionLoaded, currentOrganizationId?: number) {
        const userTimezone = this.options.currentUser.get("timezone");

        let groupedFilesByMonth;
        if (collectionLoaded && this.dailyReportFilesCollection.length) {
            const files = this.dailyReportFilesCollection.models;
            groupedFilesByMonth = this.groupFilesByMonth(files);
        }

        this.$el.html(
            this.template({
                dailyReportFilesCollection: this.dailyReportFilesCollection,
                orgId: this.orgId,
                currentOrg: this.options.organizationCollection.findWhere({ id: currentOrganizationId }),
                collectionLoaded: collectionLoaded,
                downloadFileURL: this.dailyReportFilesCollection.downloadReport(),
                userTimezone: userTimezone,
                groupedFilesByMonth: groupedFilesByMonth,
            })
        );
    }

    /**
     * Groups files by year and month based on the `key` field, adds a `fileDate` field to each file, 
     * and sorts files within each month in descending order by date.
     *
     * @param files - Array of file objects. Each file must have a `key` field in the format:
     *                `<tenant_id>/report_daily/<YYYY>/<MM>/<DD>/filename`.
     * @returns A record where the keys are in the format `YYYY-MM` (year and month),
     *          and the values are arrays of files sorted by date (newer files first).
     */
    private groupFilesByMonth(files) {
        return files
            .sort((a, b) => {
                // Extract year, month, and day from the key
                const [yearA, monthA, dayA] = a.get("key").match(/\/(\d{4})\/(\d{2})\/(\d{2})\//)?.slice(1).map(Number) || [];
                const [yearB, monthB, dayB] = b.get("key").match(/\/(\d{4})\/(\d{2})\/(\d{2})\//)?.slice(1).map(Number) || [];
    
                // Sort by year, then month, then day in descending order
                return (
                    yearB - yearA || // Compare by year
                    monthB - monthA || // If same year, compare by month
                    dayB - dayA // If same year and month, compare by day
                );
            })
            .map((file) => {
                // Extract year, month, and day from the key
                const match = file.get("key").match(/\/(\d{4})\/(\d{2})\/(\d{2})\//);
                if (match) {
                    // Add a 'date' field in YYYY-MM-DD format
                    file.set("fileDate", `${match[1]}-${match[2]}-${match[3]}`);
                }
                return file;
            })
            .reduce((groups, file) => {
                // Extract the year and month from the key using a regular expression
                const match = file.get("key").match(/\/(\d{4})\/(\d{2})\//);
                if (!match) {
                    return groups; // Skip files with invalid keys
                }
    
                const yearMonthKey = `${match[1]}-${match[2]}`; // Format: YYYY-MM
    
                if (!groups[yearMonthKey]) {
                    groups[yearMonthKey] = [];
                }
    
                // Add the file to the group corresponding to the year-month
                groups[yearMonthKey].push(file);
    
                return groups;
            }, {} as Record<string, typeof files>);
    }       

    private handleDownloadReportFile(e: JQuery.UIEventBase) {
        const button = $(e.currentTarget);
        const fileKey = $(e.currentTarget).parents(".table__row").attr("data-key");
        console.log("Downloading file:", fileKey);

        // Set loading state
        button.find(".button__text").html("Downloading...");
        button.prop("disabled", true);

        $.ajax({
            type: "POST",
            url: this.dailyReportFilesCollection.downloadReport(),
            data: JSON.stringify({
                s3Key: fileKey,
            }),
            contentType: "application/json; charset=utf-8",
            xhrFields: {
                responseType: "blob", // Receive binary file data
            },
            success: function (data, textStatus, xhr) {
                const disposition = xhr.getResponseHeader("Content-Disposition");
                let filename = "downloaded-file"; // Default filename
                if (disposition) {
                    const matches = /filename="([^"]*)"/.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1];
                }

                // Create a Blob from the response
                const blob = new Blob([data], { type: xhr.getResponseHeader("Content-Type") });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

                // Remove loading state
                button.find(".button__text").html("Download PDF");
                button.prop("disabled", false);
            },
            error: function (jqXHR, textStatus, errorThrown) {
                console.error("Error:", textStatus, errorThrown);
                (<any>$).notify(
                    {
                        title: "Something went wrong",
                        message: "Failed to download the file",
                        icon: "fa fa-exclamation-circle",
                    },
                    {
                        animate: {
                            enter: "animated fadeInUp",
                        },
                        // The type property defines the css style
                        // See alerts.less for available styles
                        type: `warning-alt`,
                        placement: { from: "bottom", align: "center" },
                        delay: 4000,
                    }
                );

                // Remove loading state
                button.find(".button__text").html("Download PDF");
                button.prop("disabled", false);
            },
        });
    }

    private handleToggleMonth(e: JQuery.UIEventBase) {
        const button = $(e.currentTarget);
        const month = button.attr("data-month");
        const rows = $("tr[data-month='row-" + month + "']");

        // Toggle visibility of the rows for the selected month
        rows.toggleClass("hidden");
        rows.toggleClass("expanded");

        // Update the +/- sign to show expand/collapse status
        const toggleIcon = button.find(".month-toggle-icon");
        if (rows.first().hasClass("hidden")) {
            toggleIcon.text("+"); // Show plus when collapsed
        } else {
            toggleIcon.text("-"); // Show minus when expanded
        }
    }

    remove() {
        // while (this.subViews.length) this.subViews.pop().remove();
        // But also do what the super view would normally do
        return super.remove();
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/reports/dailyReports/DailyReportsView.ts